import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  apiService, ReplenishmentCausa, ReplenishmentOrder, ReplenishmentOrderDetail,
  ReplenishmentOrdersResponse,
} from '../services/api';
import {
  CAUSAS, DESTINOS, EM_CURSO_DIAS, ONLINE_CAUSAS, ONLINE_STORE, PHYSICAL_STORES, STORE_CAUSAS, TOTAL, estadoSap, fmtEur, fmtHours,
  fmtInt, fmtLocalDateTime, storeLabel,
} from '../utils/replenishment';

const PAGE_SIZE = 50;

export const CausaBadge: React.FC<{ causa: ReplenishmentCausa }> = ({ causa }) => {
  const meta = CAUSAS[causa];
  return <span className={`badge ${meta?.badge || 'bg-secondary'} me-1`} title={meta?.hint}>{meta?.label || causa}</span>;
};

const ResultBadge: React.FC<{ hit: boolean | null; excluded?: boolean }> = ({ hit, excluded }) => {
  if (excluded) return <span className="badge bg-dark">Excluída</span>;
  return hit
    ? <span className="badge bg-success">Acerto</span>
    : <span className="badge bg-danger">Falha</span>;
};

const RealBadge: React.FC<{ hit: boolean | null; pending?: boolean | null }> = ({ hit, pending }) => {
  if (hit === null || hit === undefined) return <span className="text-muted">-</span>;
  if (pending) return <span className="badge bg-warning text-dark">Pendente</span>;
  return hit
    ? <span className="badge bg-success">Acerto real</span>
    : <span className="badge bg-danger">Falha real</span>;
};

// Linha do tempo dos estados SAP de uma linha (hora da carga, precisão ~1 h)
const LineTimeline: React.FC<{ line: ReplenishmentOrderDetail['lines'][number] }> = ({ line }) => {
  const steps: Array<[string, string | null]> = [
    ['Criada', line.sap_first_seen_ts],
    ['Aguardar produto', line.sap_prod_ts],
    ['Pronta', line.sap_leva_ts],
    ['Levantada/enviada', line.sap_env_ts],
  ];
  const seen = steps.filter(([, ts]) => ts);
  if (seen.length === 0) return <span className="text-muted">sem histórico</span>;
  return (
    <span className="small">
      {seen.map(([label, ts], i) => (
        <span key={label}>
          {i > 0 && <span className="text-muted"> → </span>}
          <span className={label === 'Aguardar produto' ? 'text-danger' : ''}>{label}</span>{' '}
          <span className="text-muted">{fmtLocalDateTime(ts)}</span>
        </span>
      ))}
    </span>
  );
};

const DestinoBadge: React.FC<{ destino: string | null | undefined; dias?: number | null }> = ({ destino, dias }) => {
  if (!destino) return <span className="text-muted">-</span>;
  const meta = DESTINOS[destino];
  const emCurso = (destino === 'aberta' || destino === 'pronta_nao_levantada')
    && dias !== null && dias !== undefined && dias < EM_CURSO_DIAS;
  return (
    <span className={`badge ${emCurso ? 'bg-light text-dark border' : meta?.badge || 'bg-secondary'}`}
          title={emCurso ? `Menos de ${EM_CURSO_DIAS} dias: ainda em curso` : undefined}>
      {meta?.label || destino}{emCurso ? ' (em curso)' : ''}
    </span>
  );
};

interface DetailModalProps {
  doc: string;
  onClose: () => void;
}

export const OrderDetailModal: React.FC<DetailModalProps> = ({ doc, onClose }) => {
  const [detail, setDetail] = useState<ReplenishmentOrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    apiService.getReplenishmentOrderDetail(doc)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Erro ao carregar'); });
    return () => { cancelled = true; };
  }, [doc]);

  const order = detail?.order;
  const snapshot = detail?.lines[0]?.snapshot_ts;
  const usedCausas = Array.from(new Set(detail?.lines.map((l) => l.causa) ?? []));
  // Linhas do site primeiro; as acrescentadas na loja vão para o fim
  const sortedLines = [...(detail?.lines ?? [])].sort(
    (a, b) => Number(!!a.added_in_store) - Number(!!b.added_in_store) || a.sku.localeCompare(b.sku));
  const addedCount = sortedLines.filter((l) => l.added_in_store).length;

  return (
    <>
      <div className="modal d-block" tabIndex={-1} role="dialog" onClick={onClose}>
        <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                Encomenda {order?.external_ref || ''} <span className="text-muted small">(doc {doc})</span>
              </h5>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger">{error}</div>}
              {!detail && !error && <div className="text-muted">A carregar...</div>}
              {order && detail && (
                <>
                  <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
                    <ResultBadge hit={order.order_hit} excluded={order.order_excluded} />
                    <span>{storeLabel(order.loja)}</span>
                    <span>{fmtLocalDateTime(order.order_ts)}</span>
                    <span>
                      {order.n_skus} SKU{order.n_skus === 1 ? '' : 's'}
                      {addedCount > 0 && <span className="text-muted"> (+{addedCount} acrescentada{addedCount === 1 ? '' : 's'} na loja)</span>}
                    </span>
                    <span>{fmtEur(order.order_net, 2)}</span>
                    {order.order_hit === false && order.order_hit_se_t && (
                      <span className="badge bg-warning text-dark" title="Teria sido acerto se o T000 contasse">
                        Hit potencial com T000
                      </span>
                    )}
                    <RealBadge hit={order.order_hit_sap} pending={order.pendente} />
                    <span>Destino: <DestinoBadge destino={order.destino_final} /></span>
                    {order.order_hit_sap !== null && (
                      <span title="Desde a encomenda até todas as linhas do site estarem prontas (hora da carga SAP, ~1 h)">
                        Espera: <strong>{order.pendente ? 'ainda pendente' : fmtHours(order.espera_horas)}</strong>
                        {order.order_pronta_ts_sap && (
                          <span className="text-muted small"> (pronta {fmtLocalDateTime(order.order_pronta_ts_sap)})</span>
                        )}
                      </span>
                    )}
                    <span className="text-muted small">
                      Snapshot de stock usado: {fmtLocalDateTime(snapshot)}
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>SKU</th><th>Nome</th><th>Categoria</th><th>Estado</th>
                          <th className="text-end">Qtd</th><th className="text-end">Valor</th>
                          <th className="text-end">Stock {order.loja === ONLINE_STORE ? 'T000' : 'loja'}</th>
                          {order.loja !== ONLINE_STORE && <th className="text-end">Stock T000</th>}
                          <th className="text-end" title="Lojas A000–F000 com stock">Lojas c/ stock</th>
                          <th>Resultado</th><th>Causa</th><th>Destino</th><th>Estados SAP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLines.map((l) => (
                          <tr key={l.sku}
                              className={l.added_in_store || l.line_cancelada ? 'table-secondary text-muted' : l.line_hit ? '' : 'table-danger'}>
                            <td><Link to={`/products/${encodeURIComponent(l.sku)}`}>{l.sku}</Link></td>
                            <td style={{ maxWidth: 320 }}>{l.nome || '-'}</td>
                            <td className="small">{[l.cat1, l.cat2].filter(Boolean).join(' › ') || '-'}</td>
                            <td className="small">{l.estado || '-'}</td>
                            <td className="text-end">{fmtInt(l.qty)}</td>
                            <td className="text-end">{fmtEur(l.net, 2)}</td>
                            <td className="text-end">{fmtInt(l.stock_loja)}</td>
                            {order.loja !== ONLINE_STORE && <td className="text-end">{fmtInt(l.stock_t)}</td>}
                            <td className="text-end" title={`Stock total nas lojas: ${fmtInt(l.stock_lojas_total)}`}>
                              {l.lojas_com_stock}
                            </td>
                            <td>{l.added_in_store || l.line_cancelada ? '—' : l.line_hit
                              ? <span className="badge bg-success">Acerto</span>
                              : <span className="badge bg-danger">Falha</span>}</td>
                            <td><CausaBadge causa={l.causa} /></td>
                            <td>
                              <DestinoBadge destino={l.destino_linha} />
                              {l.motivo_rejeicao && (
                                <span className="text-muted small ms-1" title="Motivo de rejeição SAP (SALESDOCUMENTRJCNREASON)">
                                  [{l.motivo_rejeicao}]
                                </span>
                              )}
                            </td>
                            <td><LineTimeline line={l} />
                              {l.estado_actual_sap && <span className="badge bg-light text-dark border ms-1">{estadoSap(l.estado_actual_sap)}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {addedCount > 0 && (
                    <div className="alert alert-secondary py-2 small">
                      Linha criada no levantamento, não fazia parte da encomenda do site, não conta para o hit rate.
                    </div>
                  )}
                  <div className="small text-muted">
                    {usedCausas.filter((c) => c !== 'ok').map((c) => (
                      <div key={c}><CausaBadge causa={c} /> {CAUSAS[c]?.hint}</div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
};

interface Props {
  start: string;
  end: string;
  /** Lojas cujas encomendas listar (TOTAL = lojas físicas seleccionadas). */
  stores: string[];
  scopeCode: string;       // loja ou TOTAL, para o título
}

const ReplenishmentOrders: React.FC<Props> = ({ start, end, stores, scopeCode }) => {
  const [params, setParams] = useSearchParams();
  const result = params.get('result') || 'all';
  const ordering = params.get('order') || 'date_desc';
  const destino = params.get('destino') || '';
  const pendingDays = Math.max(0, parseInt(params.get('pdays') || '0', 10) || 0);
  const causa = params.get('causa') || '';
  const search = params.get('q') || '';
  const page = Math.max(0, parseInt(params.get('page') || '0', 10) || 0);
  const doc = params.get('doc');

  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState<ReplenishmentOrdersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storesKey = stores.join(',');

  // Actualiza um conjunto de query params (preservando os restantes); '' remove.
  const patch = useCallback((changes: Record<string, string>, push = false) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: !push });
  }, [setParams]);

  useEffect(() => { setSearchInput(search); }, [search]);

  const [exporting, setExporting] = useState<'orders' | 'lines' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Exporta tudo o que os filtros activos apanham (independente da página)
  const exportCsv = async (level: 'orders' | 'lines') => {
    setExporting(level);
    setExportError(null);
    try {
      const allPhysical = scopeCode === TOTAL && stores.length === PHYSICAL_STORES.length;
      const { blob, filename } = await apiService.exportReplenishmentOrders({
        start, end, level,
        ...(scopeCode !== TOTAL ? { store: scopeCode } : allPhysical ? { store: TOTAL } : { stores: storesKey }),
        result: result === 'all' ? undefined : result,
        causa: causa || undefined,
        search: search || undefined,
        pending_days: result === 'pending' && pendingDays ? pendingDays : undefined,
        destino: destino || undefined,
        ordering: ordering === 'date_desc' ? undefined : ordering,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      let message = e?.message || 'Erro ao exportar';
      // Com responseType blob o corpo do erro vem como Blob JSON
      try { message = JSON.parse(await e.response.data.text()).error || message; } catch { /* mantém */ }
      setExportError(message);
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.getReplenishmentOrders({
      start, end, stores: storesKey, result, causa: causa || undefined,
      search: search || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      ordering, destino: destino || undefined,
      pending_days: result === 'pending' && pendingDays ? pendingDays : undefined,
    })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Erro ao carregar'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [start, end, storesKey, result, causa, search, page, ordering, pendingDays, destino]);

  const causaOptions = stores.length === 1 && stores[0] === ONLINE_STORE
    ? ONLINE_CAUSAS
    : stores.includes(ONLINE_STORE) ? [...STORE_CAUSAS, ...ONLINE_CAUSAS] : STORE_CAUSAS;
  const uniqueCausas = Array.from(new Set(causaOptions));
  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="card mb-3" id="replenishment-orders">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span>
          Encomendas — {storeLabel(scopeCode)}
          {data && <span className="text-muted ms-2">({fmtInt(data.count)})</span>}
        </span>
        <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => patch({ store: '', doc: '', result: '', causa: '', q: '', page: '', order: '', pdays: '', destino: '' }, true)}>
          Fechar lista
        </button>
      </div>
      <div className="card-body">
        <div className="row g-2 align-items-end mb-3">
          <div className="col-auto">
            <div className="btn-group btn-group-sm">
              {([['all', 'Todas'], ['hit', 'Acerto'], ['miss', 'Falha']] as const).map(([k, label]) => (
                <button key={k} type="button"
                        className={`btn ${result === k ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => patch({ result: k === 'all' ? '' : k, page: '' })}>
                  {label}
                </button>
              ))}
              {stores.includes(ONLINE_STORE) && (
                <button type="button"
                        className={`btn ${result === 'excluded' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => patch({ result: 'excluded', page: '' })}>
                  Excluídas
                </button>
              )}
            </div>
          </div>
          <div className="col-auto">
            <div className="btn-group btn-group-sm" title="Hit real: nenhuma linha passou por 'Aguardar produto' no SAP">
              {([['real_hit', 'Acerto real'], ['real_miss', 'Falha real'], ['diverge', 'Divergentes'], ['pending', 'Pendentes']] as const).map(([k, label]) => (
                <button key={k} type="button"
                        className={`btn ${result === k ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => patch({ result: k, page: '', order: k === 'pending' || k === 'real_miss' ? 'wait_desc' : '' })}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {result === 'pending' && (
            <div className="col-auto d-flex align-items-center gap-1 small">
              há mais de
              <input type="number" min={0} max={365} className="form-control form-control-sm" style={{ width: 70 }}
                     value={pendingDays || ''} placeholder="0"
                     onChange={(e) => patch({ pdays: e.target.value, page: '' })} />
              dias
            </div>
          )}
          <div className="col-auto">
            <select className="form-select form-select-sm" value={destino}
                    onChange={(e) => patch({ destino: e.target.value, page: '' })}>
              <option value="">Qualquer destino</option>
              {Object.entries(DESTINOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="col-auto">
            <select className="form-select form-select-sm" value={ordering}
                    onChange={(e) => patch({ order: e.target.value === 'date_desc' ? '' : e.target.value, page: '' })}>
              <option value="date_desc">Mais recentes</option>
              <option value="date_asc">Mais dias desde a encomenda</option>
              <option value="wait_desc">Maior espera</option>
              <option value="value_desc">Maior valor</option>
              <option value="skus_desc">Mais SKUs</option>
            </select>
          </div>
          <div className="col-auto">
            <select className="form-select form-select-sm" value={causa}
                    onChange={(e) => patch({ causa: e.target.value, page: '' })}>
              <option value="">Todas as causas</option>
              {uniqueCausas.map((c) => <option key={c} value={c}>{CAUSAS[c].label} — {CAUSAS[c].hint}</option>)}
            </select>
          </div>
          <div className="col-auto ms-md-auto">
            <div className="dropdown">
              <button className="btn btn-sm btn-outline-success dropdown-toggle" type="button"
                      data-bs-toggle="dropdown" aria-expanded="false" disabled={exporting !== null}>
                {exporting ? 'A exportar…' : 'Exportar CSV'}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><button className="dropdown-item" type="button" onClick={() => exportCsv('orders')}>
                  Encomendas</button></li>
                <li><button className="dropdown-item" type="button" onClick={() => exportCsv('lines')}>
                  Encomendas + todas as linhas</button></li>
              </ul>
            </div>
          </div>
          <div className="col-12 col-md-auto">
            <form className="d-flex gap-1" onSubmit={(e) => { e.preventDefault(); patch({ q: searchInput.trim(), page: '' }); }}>
              <input className="form-control form-control-sm" placeholder="Doc, encomenda ou SKU"
                     value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              <button className="btn btn-sm btn-outline-primary" type="submit">Pesquisar</button>
            </form>
          </div>
        </div>

        {exportError && <div className="alert alert-danger">Erro ao exportar: {exportError}</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        {loading && <div className="text-muted small mb-2">A carregar...</div>}

        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-2">
            <thead className="table-light">
              <tr>
                <th>Data/hora</th>
                <th>Documento</th>
                <th>Encomenda site</th>
                {stores.length > 1 && <th>Loja</th>}
                <th className="text-end">SKUs</th>
                <th className="text-end">Valor</th>
                <th>Resultado</th>
                <th>Real (SAP)</th>
                <th className="text-end">Espera</th>
                <th>Estado SAP</th>
                <th>Destino</th>
                <th className="text-end">Dias</th>
                <th>Causas</th>
              </tr>
            </thead>
            <tbody>
              {data?.results.map((o: ReplenishmentOrder) => (
                <tr key={`${o.doc}-${o.loja}`} style={{ cursor: 'pointer' }}
                    onClick={() => patch({ doc: o.doc }, true)}>
                  <td>{fmtLocalDateTime(o.order_ts)}</td>
                  <td>{o.doc}</td>
                  <td>{o.external_ref || '-'}</td>
                  {stores.length > 1 && <td>{o.loja}</td>}
                  <td className="text-end">{o.n_skus}</td>
                  <td className="text-end">{fmtEur(o.order_net, 2)}</td>
                  <td>
                    <ResultBadge hit={o.order_hit} excluded={o.order_excluded} />
                    {o.linhas_falhadas > 0 && !o.order_excluded && (
                      <span className="text-muted small ms-1">{o.linhas_falhadas}/{o.n_skus} linhas</span>
                    )}
                  </td>
                  <td><RealBadge hit={o.order_hit_sap} pending={o.pendente} /></td>
                  <td className="text-end">{o.pendente ? '…' : fmtHours(o.espera_horas)}</td>
                  <td className="small">{estadoSap(o.estado_sap)}</td>
                  <td><DestinoBadge destino={o.destino_final} dias={o.dias_desde_encomenda} /></td>
                  <td className="text-end">{o.dias_desde_encomenda}</td>
                  <td>{o.causas.map((c) => <CausaBadge key={c} causa={c} />)}</td>
                </tr>
              ))}
              {data && data.results.length === 0 && (
                <tr><td colSpan={13} className="text-muted text-center">Sem encomendas.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {data && data.count > PAGE_SIZE && (
          <div className="d-flex justify-content-between align-items-center">
            <button className="btn btn-sm btn-outline-secondary" disabled={page === 0}
                    onClick={() => patch({ page: page > 1 ? String(page - 1) : '' })}>‹ Anterior</button>
            <span className="small text-muted">Página {page + 1} de {fmtInt(totalPages)}</span>
            <button className="btn btn-sm btn-outline-secondary" disabled={page + 1 >= totalPages}
                    onClick={() => patch({ page: String(page + 1) })}>Seguinte ›</button>
          </div>
        )}
      </div>

      {doc && <OrderDetailModal doc={doc} onClose={() => patch({ doc: '' })} />}
    </div>
  );
};

export default ReplenishmentOrders;
